import { copyProperties } from './common-utils.js';

/**
 * Manages the counting of stats like successes and failures when processing
 * multiple items.
 *
 * Because default behaviors can't cover everything, this class is kept simple.
 * You are encouraged to subclass this to extend its behavior, such as logging
 * useful debug info, retaining context data, tracking additional states besides
 * the default three, etc.
 *
 * @example
 * const summary = new Reckoning(items.length);
 * for (const item of items) {
 *   try {
 *     if (!item) {
 *       summary.skip();
 *       continue;
 *     }
 *     const result = process(item);
 *     summary.succeed(result);
 *   } catch (e: unknown) {
 *     summary.fail();
 *   }
 * }
 *
 * class SyncSummary extends Reckoning {
 *   entities = new Map<Id, Entity>();
 *   skipped = new Set<Id>();
 *   skip(id: Id): void {
 *     super.skip();
 *     this.skipped.add(id);
 *   }
 *   succeed(id: Id, entity: Entity): void {
 *     super.succeed();
 *     this.entities.add(id, entity);
 *   }
 *   fail(id: Id) {
 *     super.fail();
 *     console.error(`Failed sync for entity ${id}`);
 *   }
 * }
 */
export class Reckoning implements ReckoningLike {
  expectedTotal?: number;

  skipped: number = 0;
  succeeded: number = 0;
  failed: number = 0;

  get actualTotal(): number {
    return this.skipped + this.succeeded + this.failed;
  }

  constructor(options: ReckoningOptions = {}) {
    const { expectedTotal } = options;
    if (expectedTotal !== undefined) {
      this.expectedTotal = expectedTotal;
    }
  }

  //#region Serialization

  static dataProperties = ['expectedTotal', 'skipped', 'succeeded', 'failed'] satisfies Array<keyof Reckoning>;

  static fromJSON<T = Reckoning>(data: any): T {
    return copyProperties(new this() as any, data, this.dataProperties);
  }

  toJSON(): ReckoningLike {
    const { expectedTotal, actualTotal, skipped, succeeded, failed } = this;
    return { expectedTotal, actualTotal, skipped, succeeded, failed };
  }

  //#endregion

  //#region Bumpers

  /**
   * The methods here have the signature `(...args: any[]) => void` to allow
   * subclasses more flexibility. A simpler type `() => void` would prohibit
   * subclasses from overriding these methods and adding arguments without being
   * forced to make all arguments optional.
   */

  /** Increments the skip count. */
  skip(..._args: any[]): void {
    this.skipped++;
  }

  /** Increments the success count. */
  succeed(..._args: any[]): void {
    this.succeeded++;
  }

  /** Increments the fail count. */
  fail(..._args: any[]): void {
    this.failed++;
  }

  //#endregion

  //#region Predicates

  /** Whether `.fail` was called at least once. */
  hasFailures(): boolean {
    return this.failed > 0;
  }

  /** Whether every call was a `.fail`. */
  isCompleteFailure(): boolean {
    return this.failed > 0 && this.actualTotal === this.failed;
  }

  /** Whether this was a mixture of `.succeed` and `.fail`. */
  isPartiallySuccessful(): boolean {
    return this.succeeded > 0 && this.succeeded < this.actualTotal;
  }

  /** Whether every call was a `.succeed`. */
  isLocallySuccessful(): boolean {
    return this.succeeded === this.actualTotal;
  }

  /**
   * Whether every call was a `.succeed`, and, if an `expectedTotal` was given,
   * whether the number of calls matches that expected total.
   */
  isCompletelySuccessful(): boolean {
    return (
      this.isLocallySuccessful() && (this.expectedTotal === undefined ? true : this.succeeded === this.expectedTotal)
    );
  }

  //#endregion

  /**
   * Generates an attributes object from this summary.
   *
   * @param [prefix=''] if provided, each key in the result object is prepended
   * with the prefix
   */
  toAttributes<Prefix extends string = ''>(prefix: Prefix | '' = ''): WithPropertyPrefix<ReckoningAttributes, Prefix> {
    const { expectedTotal, actualTotal: total, skipped, succeeded, failed } = this;
    const result = {
      [`${prefix}total.expected`]: expectedTotal,
      [`${prefix}total`]: total,
      [`${prefix}skipped`]: skipped,
      [`${prefix}succeeded`]: succeeded,
      [`${prefix}failed`]: failed,
    };
    if (expectedTotal === undefined) {
      delete result[`${prefix}total.expected`];
    }
    return result as unknown as WithPropertyPrefix<ReckoningAttributes, Prefix>;
  }
}

export interface ReckoningOptions {
  expectedTotal?: number;
}

/** A flat JSON representation of `Reckoning`. */
export interface ReckoningLike {
  expectedTotal?: number;
  actualTotal: number;
  skipped: number;
  succeeded: number;
  failed: number;
}

export type ReckoningData = Partial<Omit<ReckoningLike, 'actualTotal'>>;

/**
 * Prefixes each string property of `T` with some string literal type `Prefix`.
 *
 * @example
 * type Location = WithPropertyPrefix<{ lat: number; long: number }, 'loc.'>;
 * // equivalent to:
 * type Location = { 'loc.lat': number; 'loc.long': number };
 */
export type WithPropertyPrefix<T extends object, Prefix extends string> = {
  [K in keyof T as `${Prefix}${K & string}`]: T[K];
};

/**
 * A more hierarchical version of `ReckoningLike`. Use `WithPropertyPrefix` to
 * deepen the hierarchy.
 */
export interface ReckoningAttributes {
  'total.expected'?: number;
  total: number;
  skipped: number;
  succeeded: number;
  failed: number;
}

//#region Summary

/** A flat JSON representation of `Summary`. */
export interface SummaryLike<Success, Failure, Skip = undefined> extends ReckoningLike {
  results: Success[];
  failures: Failure[];
  skips: Skip[];
}

export type SummaryData<Success, Failure, Skip = undefined> = Partial<
  Omit<SummaryLike<Success, Failure, Skip>, 'actualTotal'>
>;

/**
 * A minimal logger interface for use with {@link Summary}.
 *
 * Satisfied by `pino.Logger` and its child loggers - no adapter required.
 * The `reason` field of Success/Failure/Skip values becomes the log message;
 * the remaining fields become structured bindings.
 */
export interface SummaryLogger {
  info(bindings: object, message: string): void;
  warn(bindings: object, message: string): void;
  error(bindings: object, message: string): void;
}

/**
 * Manages the counting of stats like successes and failures when processing
 * multiple items, and accumulates the result values and failure errors that
 * may arise from this process.
 *
 * You are encouraged to subclass this. In addition to the possible use cases
 * described for subclassing `Reckoning`, you can also subclass this class just
 * to make the type name shorter.
 *
 * If you provide a third type parameter, calls to {@link skip} will take a
 * value of that type and store it in {@link skips}.
 *
 * ### Automatic logging
 *
 * Pass a `logger` in the constructor options. When Success, Failure, or Skip
 * values include a `reason: string` field, each call to {@link succeed},
 * {@link fail}, or {@link skip} will emit a structured log line automatically:
 * - `succeed` and `skip` log at **info**
 * - `fail` logs at **error**
 *
 * The `reason` string becomes the log message; all other fields become bindings.
 *
 * @example
 * class PaymentSummary extends Summary<PaymentResult, PaymentGatewayError> {
 *   // ...
 * }
 *
 * const summary = new Summary<Frobulation, FrobulationError>(items.length);
 * for (const item of items) {
 *   try {
 *     if (!item) {
 *       summary.skip();
 *       continue;
 *     }
 *     const result = await frobulate(item);
 *     summary.succeed(result);
 *   } catch (e: unknown) {
 *     if (e instanceof FrobulationError) {
 *       summary.fail(e);
 *     }
 *   }
 * }
 */
export class Summary<Success, Failure, Skip = undefined>
  extends Reckoning
  implements SummaryLike<Success, Failure, Skip>
{
  results: Success[] = [];
  failures: Failure[] = [];
  skips: Skip[] = [];

  protected readonly logger?: SummaryLogger;

  constructor(options: Partial<SummaryLike<Success, Failure, Skip>> & { logger?: SummaryLogger } = {}) {
    super(options);
    this.logger = options.logger;
  }

  private log(level: 'info' | 'error', value: unknown): void {
    if (!this.logger) {
      return;
    }
    if (typeof value !== 'object' || value === null || !('reason' in value)) {
      return;
    }
    const { reason, ...bindings } = value as { reason: string } & Record<string, unknown>;
    this.logger[level](bindings, reason);
  }

  static summaryDataProperties = [...super.dataProperties, 'results', 'failures', 'skips'] satisfies Array<
    keyof Summary<any, any, any>
  >;

  static override fromJSON<T = Summary<any, any, any>>(data: any): T {
    return copyProperties(new this() as any, data, this.summaryDataProperties);
  }

  override toJSON(): SummaryLike<Success, Failure, Skip> {
    const { results, failures, skips } = this;
    return { ...super.toJSON(), results, failures, skips };
  }

  /** Increments the skip count, possibly with a reason. */
  override skip(...[value]: Skip extends undefined ? [] : [Skip]): void {
    super.skip();
    if (value) {
      this.skips.push(value);
      this.log('info', value);
    }
  }

  /** Increments the success count with a value. */
  override succeed(result: Success): void {
    super.succeed();
    this.results.push(result);
    this.log('info', result);
  }

  /** Increments the failure count with a reason. */
  override fail(failure: Failure): void {
    super.fail();
    this.failures.push(failure);
    this.log('error', failure);
  }
}

//#endregion
